import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dumbbell, Send, Loader2, ThumbsUp, Pencil, Trash2, Plus, GraduationCap, RotateCcw, Lightbulb, X, Check } from 'lucide-react';

export const PalestraAI = () => {
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [knowledge, setKnowledge] = useState([]);
  const [newRule, setNewRule] = useState('');
  const [correcting, setCorrecting] = useState(null);
  const [correction, setCorrection] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [suggEdits, setSuggEdits] = useState({});
  const bottomRef = useRef(null);

  const loadSuggestions = async () => {
    try {
      const r = await api.get('/ai-assistant/suggerimenti');
      setSuggestions(r.data);
    } catch { /* silente */ }
  };

  const generaSuggerimenti = async () => {
    setGenLoading(true);
    try {
      const r = await api.post('/ai-assistant/suggerimenti/genera', {}, { timeout: 120000 });
      setSuggestions(r.data);
      setSuggEdits({});
      if (!(r.data.items || []).length) toast.info(r.data.motivo || 'Nessun suggerimento: l\'AI sta gestendo bene le richieste!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore generazione suggerimenti'); }
    finally { setGenLoading(false); }
  };

  const insegnaSuggerimento = async (item, idx) => {
    const risposta = (suggEdits[idx] ?? item.risposta_suggerita).trim();
    if (!risposta) { toast.error('La risposta non può essere vuota'); return; }
    try {
      await api.post('/ai-assistant/knowledge', { tipo: 'esempio', domanda: item.domanda, risposta });
      await api.post('/ai-assistant/suggerimenti/scarta', { index: idx });
      toast.success('Insegnato! L\'AI userà questa risposta come esempio');
      loadKnowledge();
      loadSuggestions();
    } catch { toast.error('Errore salvataggio'); }
  };

  const scartaSuggerimento = async (idx) => {
    try {
      const r = await api.post('/ai-assistant/suggerimenti/scarta', { index: idx });
      setSuggestions(s => ({ ...s, items: r.data.items }));
    } catch { toast.error('Errore'); }
  };

  const loadKnowledge = async () => {
    try {
      const r = await api.get('/ai-assistant/knowledge');
      setKnowledge(r.data.knowledge || []);
    } catch { /* silente */ }
  };

  useEffect(() => { loadKnowledge(); loadSuggestions(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    const next = [...chat, { role: 'cliente', text }];
    setChat(next);
    setInput('');
    setThinking(true);
    try {
      const r = await api.post('/ai-assistant/trainer/chat', { messages: next.map(m => ({ role: m.role, text: m.text })) }, { timeout: 120000 });
      const d = r.data;
      setChat(c => [...c, { role: 'ai', text: d.risposta, azione: d.azione, oggetto: d.oggetto, domanda: text }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore AI');
      setChat(c => c.slice(0, -1));
      setInput(text);
    } finally { setThinking(false); }
  };

  const approva = async (msg) => {
    try {
      await api.post('/ai-assistant/knowledge', { tipo: 'esempio', domanda: msg.domanda, risposta: msg.text });
      toast.success('Risposta approvata: l\'AI la usera\' come esempio');
      loadKnowledge();
    } catch { toast.error('Errore salvataggio'); }
  };

  const salvaCorrezione = async (msg) => {
    if (!correction.trim()) { toast.error('Scrivi la risposta corretta'); return; }
    try {
      await api.post('/ai-assistant/knowledge', { tipo: 'esempio', domanda: msg.domanda, risposta: correction.trim() });
      toast.success('Correzione appresa!');
      setCorrecting(null); setCorrection('');
      loadKnowledge();
    } catch { toast.error('Errore salvataggio'); }
  };

  const aggiungiRegola = async () => {
    if (!newRule.trim()) return;
    try {
      await api.post('/ai-assistant/knowledge', { tipo: 'regola', testo: newRule.trim() });
      toast.success('Regola appresa!');
      setNewRule('');
      loadKnowledge();
    } catch { toast.error('Errore salvataggio'); }
  };

  const eliminaKnowledge = async (kid) => {
    try {
      await api.delete(`/ai-assistant/knowledge/${kid}`);
      loadKnowledge();
    } catch { toast.error('Errore eliminazione'); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="palestra-ai">
      <Card className="flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><Dumbbell size={15} /> Conversa e addestra</CardTitle>
            {chat.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setChat([])} data-testid="trainer-reset-btn">
                <RotateCcw size={13} className="mr-1" /> Nuova conversazione
              </Button>
            )}
          </div>
          <p className="text-[11px] text-slate-400">Scrivi come se fossi un cliente. Se la risposta va bene premi 👍 (diventa un esempio da imitare), se e' sbagliata correggila: l'AI imparera' per le conversazioni reali. Nulla viene inviato su WhatsApp.</p>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto space-y-3 min-h-[280px] max-h-[420px] pr-1" data-testid="trainer-chat-window">
            {chat.length === 0 && <p className="text-xs italic text-slate-400 mt-8 text-center">Inizia scrivendo un messaggio come farebbe un cliente...</p>}
            {chat.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'cliente' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.role === 'cliente' ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                  {m.role === 'ai' && m.azione !== 'rispondi' && (
                    <Badge className={`border-0 text-[10px] mb-1 ${m.azione === 'operatore' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'}`}>
                      {m.azione === 'operatore' ? `Passa a operatore${m.oggetto ? ` - ${m.oggetto}` : ''}` : 'Resterebbe in silenzio'}
                    </Badge>
                  )}
                  <p className="whitespace-pre-wrap">{m.text || (m.azione === 'silenzio' ? '(nessuna risposta)' : '')}</p>
                  {m.role === 'ai' && m.text && (
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => approva(m)} className="text-[11px] flex items-center gap-1 text-emerald-600 hover:text-emerald-800" data-testid={`trainer-approva-${i}`}>
                        <ThumbsUp size={11} /> Va bene
                      </button>
                      <button onClick={() => { setCorrecting(i); setCorrection(''); }} className="text-[11px] flex items-center gap-1 text-amber-600 hover:text-amber-800" data-testid={`trainer-correggi-${i}`}>
                        <Pencil size={11} /> Correggi
                      </button>
                    </div>
                  )}
                  {correcting === i && (
                    <div className="mt-2 space-y-1.5">
                      <Textarea rows={3} value={correction} onChange={e => setCorrection(e.target.value)}
                        placeholder="Scrivi la risposta come l'avresti data tu..." className="text-xs bg-white" data-testid="trainer-correzione-input" />
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => salvaCorrezione(m)} data-testid="trainer-salva-correzione">Salva e impara</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setCorrecting(null)}>Annulla</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {thinking && <div className="flex justify-start"><div className="bg-slate-100 rounded-2xl px-3.5 py-2"><Loader2 size={15} className="animate-spin text-slate-400" /></div></div>}
            <div ref={bottomRef} />
          </div>
          <div className="flex gap-2 mt-3">
            <Input value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
              placeholder="Scrivi come un cliente..." data-testid="trainer-input" />
            <Button onClick={sendMessage} disabled={thinking} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="trainer-send-btn">
              <Send size={14} />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><GraduationCap size={15} /> Cosa ha imparato ({knowledge.length})</CardTitle>
          <p className="text-[11px] text-slate-400">Regole ed esempi che l'AI applica nelle conversazioni reali con i clienti.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newRule} onChange={e => setNewRule(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') aggiungiRegola(); }}
              placeholder='Insegna una regola, es: "Da noi il tagliando polizza si ritira in agenzia"' data-testid="regola-input" />
            <Button variant="outline" onClick={aggiungiRegola} className="border-violet-300 text-violet-700" data-testid="regola-add-btn"><Plus size={14} /></Button>
          </div>
          <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1" data-testid="knowledge-list">
            {knowledge.length === 0 && <p className="text-xs italic text-slate-400">Ancora nessuna conoscenza appresa.</p>}
            {knowledge.map(k => (
              <div key={k.id} className="border rounded-lg p-2.5 bg-slate-50/60 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Badge className={`border-0 text-[10px] mb-1 ${k.tipo === 'regola' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {k.tipo === 'regola' ? 'Regola' : 'Esempio'}
                  </Badge>
                  {k.tipo === 'regola'
                    ? <p className="text-xs text-slate-700">{k.testo}</p>
                    : <div className="text-xs space-y-0.5">
                        {k.domanda && <p className="text-slate-500 truncate">Cliente: {k.domanda}</p>}
                        <p className="text-slate-700 whitespace-pre-wrap line-clamp-3">{k.risposta}</p>
                      </div>}
                </div>
                <button onClick={() => eliminaKnowledge(k.id)} className="text-slate-300 hover:text-red-500 shrink-0" data-testid={`knowledge-del-${k.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Suggerimenti addestramento settimanali */}
      <Card className="lg:col-span-2" data-testid="suggerimenti-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-sm flex items-center gap-2"><Lightbulb size={15} className="text-amber-500" /> Cosa insegnarle questa settimana</CardTitle>
              <p className="text-[11px] text-slate-400 mt-1">L'AI analizza le conversazioni reali degli ultimi 7 giorni e ti propone le domande gestite peggio. Generato automaticamente ogni lunedì (anche nel report email) o quando vuoi tu.</p>
            </div>
            <Button size="sm" variant="outline" onClick={generaSuggerimenti} disabled={genLoading} className="border-amber-300 text-amber-700" data-testid="genera-suggerimenti-btn">
              {genLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Lightbulb size={13} className="mr-1.5" />}
              {genLoading ? 'Analisi in corso (30-60s)...' : 'Analizza ora'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!(suggestions?.items || []).length ? (
            <p className="text-xs italic text-slate-400" data-testid="suggerimenti-empty">
              {suggestions?.generated_at
                ? `Nessun suggerimento in sospeso${suggestions?.motivo ? ` (${suggestions.motivo})` : ': ottimo, l\'AI sta gestendo bene le richieste!'}`
                : 'Premi "Analizza ora" per la prima analisi delle conversazioni.'}
            </p>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {suggestions.items.map((item, idx) => (
                <div key={idx} className="border border-amber-200 bg-amber-50/40 rounded-lg p-3 space-y-2" data-testid={`suggerimento-${idx}`}>
                  <p className="text-xs font-semibold text-slate-800">❓ {item.domanda}</p>
                  <p className="text-[11px] text-amber-700 italic">{item.motivo}</p>
                  <Textarea
                    rows={3}
                    value={suggEdits[idx] ?? item.risposta_suggerita}
                    onChange={e => setSuggEdits(s => ({ ...s, [idx]: e.target.value }))}
                    className="text-xs bg-white"
                    data-testid={`suggerimento-risposta-${idx}`}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => insegnaSuggerimento(item, idx)} data-testid={`suggerimento-insegna-${idx}`}>
                      <Check size={12} className="mr-1" /> Insegna così
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => scartaSuggerimento(idx)} data-testid={`suggerimento-scarta-${idx}`}>
                      <X size={12} className="mr-1" /> Scarta
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
